import Link from 'next/link';
import { ArrowDown, ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

function splitTitleParts(text = '') {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= 3) return { line1: String(text || '').trim(), line2: '' };
  const midpoint = Math.ceil(words.length / 2);
  return {
    line1: words.slice(0, midpoint).join(' '),
    line2: words.slice(midpoint).join(' '),
  };
}

export default function ContactHeroSection() {
  const { t } = useI18n();
  const titleParts = useMemo(() => splitTitleParts(t('contactPage.hero.title', '')), [t]);

  const scrollToContact = () => {
    const target = document.getElementById('contact-form');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section
      className="relative flex min-h-[80vh] items-center justify-center overflow-hidden border-b border-white/5 bg-[var(--dash-blacker)] pt-40 pb-20 sm:pb-24"
    >
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--dash-blacker)] via-[var(--dash-black)]/95 to-[var(--dash-blacker)]" />
        <div className="absolute inset-0 opacity-[0.03] bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%]" />
        <div className="absolute left-1/2 top-1/2 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ff5d10]/10 blur-[120px]" />
      </div>

      <motion.div
        className="relative z-10 mx-auto max-w-7xl px-6 text-center lg:px-10"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-120px' }}
      >
        <motion.div variants={itemVariants} className="mx-auto w-fit">
          <AnimatedEyebrow>{t('contactPage.hero.eyebrow')}</AnimatedEyebrow>
        </motion.div>

        <motion.h1
          variants={itemVariants}
          className="mt-8 text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] text-white sm:text-5xl lg:text-6xl"
        >
          <span className="block">{titleParts.line1 || t('contactPage.hero.title')}</span>
          {titleParts.line2 ? (
            <span className="block bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
              {titleParts.line2}
            </span>
          ) : null}
        </motion.h1>

        <motion.p variants={itemVariants} className="mx-auto mt-8 max-w-3xl text-lg leading-relaxed text-slate-400 sm:text-xl">
          {t('contactPage.hero.description')}
        </motion.p>

        <motion.div variants={itemVariants} className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <button
            type="button"
            onClick={scrollToContact}
            className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-[var(--dash-main)] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white shadow-[0_16px_36px_rgba(255,93,16,0.2)] transition-all hover:bg-[var(--dash-other)] hover:pr-14 hover:text-[var(--dash-black)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--dash-blacker)] active:scale-95"
          >
            <span className="relative z-10">{t('contactPage.form.sendMessage')}</span>
            <ChevronRight className="absolute right-5 h-5 w-5 translate-x-8 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
          </button>
          <Link
            href="/machines/categories"
            className="inline-flex items-center rounded-full border border-white/20 bg-white/5 px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-all hover:border-[var(--dash-other)] hover:bg-[var(--dash-other)] hover:text-[var(--dash-black)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dash-main)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--dash-blacker)]"
          >
            {t('contactPage.cta.viewProducts')}
          </Link>
        </motion.div>

        <motion.button
          variants={itemVariants}
          type="button"
          onClick={scrollToContact}
          className="mt-16 animate-bounce text-slate-600 transition hover:text-slate-400"
          aria-label={t('contactPage.form.sendMessage')}
        >
          <ArrowDown className="mx-auto h-6 w-6" />
        </motion.button>
      </motion.div>
    </section>
  );
}
